import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { isUndefined } from 'lodash';
import * as React from 'react';

import { Alert, AlertSize, AlertType } from '../../components/Alert/Alert';
import { Loader } from '../../components/Loader/Loader';
import { ModalDecorator } from '../../components/ModalDecorator/ModalDecorator';
import { Tab, TabList, TabPanel, Tabs, TabsSize } from '../../components/Tabs/Tabs';
import { Bem } from '../../service/Bem/Bem';
import { ValidationErrorData } from '../../typings/common';
import { CanvasAppContext, GALLERY_Z_INDEX_LEVEL, ResourceType } from '../common/typings';
import { GalleryPaneContainer } from '../GalleryPane/GalleryPaneContainer';
import { EmptyGalleryPanesRecord, GalleryPaneTypes } from '../GalleryPane/typings';

import './Gallery.css';
import * as keyset from './Gallery.i18n';

const i18n = i18nFactory(keyset);

const TABS_TITLES = {
    [GalleryPaneTypes.recent]: i18n('recently-used-tab-name'),
    [GalleryPaneTypes.stock]: i18n('default-tab-name'),
    [GalleryPaneTypes.idea]: i18n('idea-tab-name')
};

export interface GalleryProps {
    resourceType: ResourceType;
    currentPaneType: GalleryPaneTypes;
    isPopupOpened: boolean;
    enabledGalleryTypes: GalleryPaneTypes[];
    emptyGalleriesMap: EmptyGalleryPanesRecord;
    paneFilterRenderer: (paneType: GalleryPaneTypes) => React.ReactNode;
    header: string;
    parentBlockName?: string;
    isBusy: boolean;

    selectTab: (currentPaneType: GalleryPaneTypes) => void;
    closeGalleryPopup: () => void;
    itemRenderer: (index: number) => React.ReactNode;
    error?: ValidationErrorData;

    fetchInitialData: (resourceType: ResourceType) => void;
}

const BLOCK = 'gallery';

export class Gallery extends React.PureComponent<GalleryProps> {
    private galleryRef: React.RefObject<HTMLDivElement> = React.createRef();

    render() {
        const { currentPaneType, isPopupOpened, enabledGalleryTypes, closeGalleryPopup, header, isBusy } = this.props;
        const selectedIndex = enabledGalleryTypes.indexOf(currentPaneType);

        if (!isPopupOpened) {
            return null;
        }

        return (
            <ModalDecorator
                cls={Bem.cls(BLOCK, 'modal-popup')}
                theme={'dark-paranja'}
                visible={isPopupOpened}
                hasClose={true}
                onClose={closeGalleryPopup}
                header={header}
                zIndexGroupLevel={GALLERY_Z_INDEX_LEVEL}
            >
                {isBusy && <Loader size={'s'} />}
                <div className={Bem.cls(BLOCK)} ref={this.galleryRef}>
                    <CanvasAppContext.Provider value={{ parent: this.getRef }}>
                        <Tabs size={TabsSize.s} selectedIndex={selectedIndex} onSelect={this.onSelectTab}>
                            {this.renderTabsList()}
                            {this.renderTabPanels()}
                            {this.renderError()}
                        </Tabs>
                    </CanvasAppContext.Provider>
                </div>
            </ModalDecorator>
        );
    }

    componentDidMount(): void {
        this.props.fetchInitialData(this.props.resourceType);
    }

    private getRef = () => (this.galleryRef && this.galleryRef.current ? this.galleryRef.current : document.body);

    private onSelectTab = (index: number) => {
        const { enabledGalleryTypes } = this.props;

        this.props.selectTab(enabledGalleryTypes[index]);
    };

    private renderTabPanels() {
        const { enabledGalleryTypes, currentPaneType, paneFilterRenderer, itemRenderer, parentBlockName } = this.props;

        const extraContentClass = parentBlockName ? Bem.cls(parentBlockName, 'modal-content') : '';

        if (enabledGalleryTypes.length === 1) {
            const paneFilters = paneFilterRenderer(enabledGalleryTypes[0]);

            return (
                <div className={classNames(Bem.cls(BLOCK, 'pane', { selected: true }), extraContentClass)}>
                    <GalleryPaneContainer
                        paneType={enabledGalleryTypes[0]}
                        itemRenderer={itemRenderer}
                        paneFilters={paneFilters}
                    />
                </div>
            );
        }

        return enabledGalleryTypes.map((galleryType, index) => {
            const paneFilters = paneFilterRenderer(galleryType);

            return (
                <TabPanel
                    key={`panel_${index}`}
                    className={classNames(
                        Bem.cls(BLOCK, 'pane', {
                            selected: galleryType === currentPaneType
                        }),
                        extraContentClass
                    )}
                >
                    <GalleryPaneContainer
                        paneType={galleryType}
                        paneFilters={paneFilters}
                        itemRenderer={itemRenderer}
                    />
                </TabPanel>
            );
        });
    }

    private renderTabsList() {
        const { enabledGalleryTypes, emptyGalleriesMap } = this.props;

        if (enabledGalleryTypes.length === 1) {
            return;
        }

        return (
            <div className={Bem.cls(BLOCK, 'tabs')}>
                <TabList>
                    {enabledGalleryTypes.map((galleryType, index) => {
                        const isGalleryEmpty = emptyGalleriesMap[galleryType];
                        const isDisabled = isUndefined(isGalleryEmpty) || isGalleryEmpty;

                        return (
                            <Tab key={`tab_${index}`} disabled={isDisabled}>
                                {TABS_TITLES[galleryType]}
                            </Tab>
                        );
                    })}
                </TabList>
            </div>
        );
    }

    private renderError = (): React.ReactNode => {
        const { error } = this.props;

        if (!error) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'error')}>
                <Alert type={AlertType.error} size={AlertSize.s}>
                    {error.text}
                </Alert>
            </div>
        );
    };
}
